import { useEffect, useRef, useState } from "react"
import Cropper, { Area } from "react-easy-crop"

import useLocalStorage from "@hooks/useLocalStorage"

import { faIcon } from "@utils/faIcon"
import { getCroppedImg } from "@utils/imageCrop"

import { Button, HintTrigger, Icon } from "@atoms"

import { AlertBanner, Slider } from "@molecules"
import Dialog from "@molecules/Dialog/Dialog"
import HiddenImageInput from "@molecules/Form/inputs/HiddenImageInput/HiddenImageInput"
import { HiddenImageInputRef, ImageDimensions } from "@molecules/Form/inputs/HiddenImageInput/HiddenImageInput.types"
import toast from "@molecules/Toast/Toast"

import { useAvatarManagement } from "@pages/Settings/SettingsProfile/components/AvatarManagement/AvatarManagement.store"
import { AvatarFormatType } from "@pages/Settings/SettingsProfile/components/AvatarManagement/AvatarManagement.types"
import { validateImageProperties } from "@pages/Settings/SettingsProfile/components/AvatarManagement/AvatarManagement.utils"

import { TIP_DISMISSED_AVATAR_VISIBILITY_FYI } from "@constants/storage"

import styles from "./AvatarCropDialog.module.scss"

export default function AvatarCropDialog() {
    const fileInputRef = useRef<HiddenImageInputRef>(null)
    const [hasDismissedTip, setHasDismissedTip] = useLocalStorage<boolean>(TIP_DISMISSED_AVATAR_VISIBILITY_FYI, false)

    const {
        imageToCrop,
        imageFormatType,
        isUploadingImage,
        setImageToCrop,
        setDisplayedAvatar,
        setImageFormatType,
        updateAvatarMutation,
        isDialogOpen,
        setIsDialogOpen,
    } = useAvatarManagement()

    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState<number>(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()

    // this state is a workaround that fixes this issue: https://linear.app/roopairs/issue/ROO-2592/image-adjust-crop-circle-padding-issues
    const [isCropperShown, setIsCropperShown] = useState<boolean>(false)

    const markTipAsDismissed = () => {
        setHasDismissedTip(true)
    }

    useEffect(() => {
        const CROPPER_SHOWN_TIME_MS = 100

        if (isDialogOpen) {
            setTimeout(() => {
                setIsCropperShown(true)
            }, CROPPER_SHOWN_TIME_MS)
        } else {
            setIsCropperShown(false)
        }
    }, [isDialogOpen])

    useEffect(() => {
        if (!isDialogOpen) {
            setCrop({ x: 0, y: 0 })
            setZoom(1)
        }
    }, [isDialogOpen])

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels)
    }

    const handleReplaceImage = () => {
        fileInputRef.current?.selectImageFromOS()
    }

    const onImageReplaced = (image: File, imageDimensions: ImageDimensions) => {
        const fileFormat = image.type as AvatarFormatType

        const validationResult = validateImageProperties(image, imageDimensions)

        if (validationResult.valid) {
            setImageFormatType(fileFormat)

            setImageToCrop(URL.createObjectURL(image))
        } else {
            toast({
                type: "error",
                variant: "default",
                title: validationResult.message,
            })
        }
    }

    const saveCroppedImage = async () => {
        try {
            let image: string

            if (croppedAreaPixels) {
                image = await getCroppedImg({
                    imageSrc: imageToCrop,
                    pixelCrop: croppedAreaPixels,
                    imageFormatType,
                })
            } else {
                image = imageToCrop
            }

            void fetch(image)
                .then((res) => res.blob())
                .then((blob) => {
                    const imageFile = new File([blob], `filename.${blob.type.split("/")[1]}`, {
                        type: blob.type,
                    })

                    setDisplayedAvatar(image)

                    updateAvatarMutation({
                        avatar: imageFile,
                    })

                    setIsDialogOpen(false)
                })
        } catch (e) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not upload image",
            })
        }
    }

    const cropContent = (
        <div className={styles.cropContent}>
            <HiddenImageInput onFileChange={onImageReplaced} ref={fileInputRef} />
            {!hasDismissedTip && (
                <AlertBanner
                    message="Your avatar is visible to workspace members and in public contexts."
                    status="info"
                    type="tip"
                    title="FYI:"
                    isDismissable={true}
                    onDismiss={markTipAsDismissed}
                />
            )}
            <div className={styles.cropperArea} data-no-drag={true}>
                {isCropperShown && (
                    <Cropper
                        image={imageToCrop}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        cropShape="round"
                        minZoom={1}
                        maxZoom={3}
                        objectFit="contain"
                        showGrid={false}
                        restrictPosition={true}
                        zoomWithScroll={false}
                    />
                )}
            </div>

            <div className={styles.zoomWrap}>
                <div className={styles.smallIconWrap}>
                    <Icon icon={faIcon({ name: "image", style: "regular" })} container="fixedHeight" size={12} />
                </div>
                <Slider
                    value={[zoom]}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onValueChange={(value) => setZoom(value[0])}
                    className={styles.slider}
                />
                <Icon icon={faIcon({ name: "image", style: "regular" })} container="fixedHeight" size={20} />
            </div>
        </div>
    )

    return (
        <Dialog.Root isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} mode="dialog" variant="default">
            <Dialog.Content className={styles.dialog}>
                <Dialog.Header.Root>
                    <Dialog.Header.Title
                        title="Adjust avatar"
                        hint={
                            hasDismissedTip
                                ? {
                                      body: (
                                          <>
                                              <span className={styles.hintEmphasis}>FYI:</span> Your avatar is visible
                                              to workspace members and in public contexts.
                                          </>
                                      ),
                                      colorScheme: "blue",
                                      children: <HintTrigger />,
                                      defaultPlacement: "bottom",
                                  }
                                : undefined
                        }
                    />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>{cropContent}</Dialog.Body>
                <Dialog.Footer.Root>
                    <Dialog.Footer.LeftSide>
                        <Button variant="ghost" size="lg" onClick={() => setIsDialogOpen(false)}>
                            Cancel
                        </Button>
                    </Dialog.Footer.LeftSide>
                    <Dialog.Footer.RightSide>
                        <Button variant="subtle" size="lg" onClick={handleReplaceImage}>
                            Browse
                        </Button>
                        <Button
                            variant="solid"
                            size="lg"
                            onClick={saveCroppedImage as () => void}
                            isProcessing={isUploadingImage}
                        >
                            Save
                        </Button>
                    </Dialog.Footer.RightSide>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
